import type { PocInputValidity } from 'commons/types';
import { useEffect, useState } from 'react';
import { EapSettingsFormModelValidationModel } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/settings-devices-eap-settings-form.model';
import { useEapSettingsFormValidationContext } from './use-eap-settings-form-validation-context.hook';

export const useEapSettingsFormValidation = <T extends Record<string, PocInputValidity>>(initValue: T) => {
    const [eapSettingsFormValidatorError, setEapSettingsFormValidatorError] =
        useState<EapSettingsFormModelValidationModel>(structuredClone(initValue));
    const { errors } = useEapSettingsFormValidationContext();

    const handleError = (field: keyof T, validity: PocInputValidity) => {
        setEapSettingsFormValidatorError((prevState) => ({
            ...prevState,
            [field]: validity,
        }));
    };

    const handlePasswordError = ({ password, confirmPassword }: { password: string; confirmPassword: string }) => {
        if (password && confirmPassword && password !== confirmPassword) {
            handleError('confirmPassword', { state: 'error', type: ['pattern-mismatch'] });
            return;
        }
        if (confirmPassword === '') {
            return handleError('confirmPassword', { state: 'error', type: ['value-missing'] });
        }
        handleError('confirmPassword', { state: 'valid' });
    };

    const handleApiErrors = (err: EapSettingsFormModelValidationModel) => {
        setEapSettingsFormValidatorError((prevState) => ({
            ...prevState,
            ...err,
        }));
    };

    useEffect(() => {
        if (errors) {
            handleApiErrors(errors);
        }
    }, [errors]);

    return {
        eapSettingsFormValidatorError,
        handleError,
        handlePasswordError,
        hasErrors: Object.values(eapSettingsFormValidatorError).some((error) => error?.state === 'error'),
    };
};
